// Dinheiro no painel (`spec/17-frontend.md` §4.6). Centavos chegam como `number`
// inteiro (`spec/00-conventions.md` §4.4).
//
// Proibido `toFixed`, `parseFloat` e qualquer aritmética de float: a formatação é
// feita sobre o inteiro e a entrada é lida dígito a dígito.

/** `−` U+2212 (menos tipográfico), nunca o hífen. */
export const MINUS = '−';

const GROUP = /\B(?=(\d{3})+(?!\d))/g;

/**
 * `formatCents(123456)` → `R$ 1.234,56`. Negativo sai com `−` antes do símbolo.
 * `withSymbol: false` devolve só o número (`1.234,56`), para quando o `R$` já está
 * na label.
 */
export function formatCents(cents: number, options?: { withSymbol?: boolean }): string {
  if (!Number.isFinite(cents)) {
    throw new RangeError('formatCents espera um inteiro de centavos');
  }
  const rounded = Math.trunc(cents);
  const negative = rounded < 0;
  const digits = String(Math.abs(rounded)).padStart(3, '0');
  const whole = digits.slice(0, -2).replace(GROUP, '.');
  const fraction = digits.slice(-2);
  const symbol = options?.withSymbol === false ? '' : 'R$ ';
  return `${negative ? MINUS : ''}${symbol}${whole},${fraction}`;
}

/**
 * Lê o que o usuário digitou e devolve centavos inteiros. Aceita `1.234,56`,
 * `1234.56`, `R$ 10`, `10`, `−5`. Devolve `undefined` quando não há dígito nenhum
 * ou quando o resultado não cabe num inteiro seguro.
 */
export function parseCents(input: string): number | undefined {
  const raw = input.trim();
  const negative = raw.startsWith('-') || raw.startsWith(MINUS);
  const cleaned = raw.replace(/[^\d.,]/g, '');
  if (!/\d/.test(cleaned)) {
    return undefined;
  }

  // O último separador é o decimal quando sobram 1 ou 2 dígitos depois dele; qualquer
  // outro ponto ou vírgula é separador de milhar e some no `replace` abaixo.
  const sep = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
  const tail = sep === -1 ? '' : cleaned.slice(sep + 1);
  const isDecimal = /^\d{1,2}$/.test(tail);

  const whole = (isDecimal ? cleaned.slice(0, sep) : cleaned).replace(/\D/g, '');
  const fraction = isDecimal ? tail.padEnd(2, '0') : '00';
  const cents = Number(`${whole === '' ? '0' : whole}${fraction}`);
  if (!Number.isSafeInteger(cents)) {
    return undefined;
  }
  return negative ? -cents : cents;
}
